import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { List, ChevronRight } from 'lucide-react';

export const TableOfContents = ({ content }) => {
  const [headings, setHeadings] = useState([]);
  const [activeId, setActiveId] = useState('');
  const [isExpanded, setIsExpanded] = useState(true);

  // Extract headings from content
  useEffect(() => {
    if (!content) return;

    const extractedHeadings = [];
    const h2Regex = /^## (.+)$/gm;
    const h3Regex = /^### (.+)$/gm;

    let match;
    let index = 0;

    // Get all ## headings
    while ((match = h2Regex.exec(content)) !== null) {
      const text = match[1].trim();
      const id = generateId(text, index);
      extractedHeadings.push({ id, text, level: 2, index });
      index++;
    }

    // Get all ### headings
    while ((match = h3Regex.exec(content)) !== null) {
      const text = match[1].trim();
      const id = generateId(text, index);
      extractedHeadings.push({ id, text, level: 3, index });
      index++;
    }

    // Sort by position in content
    extractedHeadings.sort((a, b) => {
      const posA = content.indexOf(`${'#'.repeat(a.level)} ${a.text}`);
      const posB = content.indexOf(`${'#'.repeat(b.level)} ${b.text}`);
      return posA - posB;
    });

    setHeadings(extractedHeadings);
  }, [content]);

  // Generate URL-friendly ID
  const generateId = (text, index) => {
    return `section-${text
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '-')
      .substring(0, 50)}-${index}`;
  };

  // Track active section on scroll
  useEffect(() => {
    const handleScroll = () => {
      const headingElements = headings.map(h => ({
        id: h.id,
        element: document.getElementById(h.id)
      })).filter(h => h.element);

      const scrollPosition = window.scrollY + 150;

      for (let i = headingElements.length - 1; i >= 0; i--) {
        const { id, element } = headingElements[i];
        if (element.offsetTop <= scrollPosition) {
          setActiveId(id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check
    return () => window.removeEventListener('scroll', handleScroll);
  }, [headings]);

  // Scroll to section
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({
        top: elementPosition - offset,
        behavior: 'smooth'
      });
    }
  };

  if (headings.length < 3) return null; // Only show TOC if there are enough headings

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className="hidden xl:block fixed right-8 top-32 w-64 z-30"
    >
      <div className="bg-card/95 backdrop-blur-sm border border-silver/20 rounded-xl shadow-silver overflow-hidden">
        {/* Header */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-between p-4 border-b border-silver/20 hover:bg-silver/5 transition-colors"
          data-testid="toc-toggle"
        >
          <div className="flex items-center gap-2">
            <List className="w-4 h-4 text-maroon" />
            <span className="font-display font-semibold text-sm text-foreground">
              In This Chapter
            </span>
          </div>
          <motion.div
            animate={{ rotate: isExpanded ? 90 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          </motion.div>
        </button>

        {/* TOC List */}
        <motion.div
          initial={false}
          animate={{ height: isExpanded ? 'auto' : 0 }}
          className="overflow-hidden"
        >
          <nav className="p-3 max-h-[60vh] overflow-y-auto scrollbar-thin" data-testid="toc-nav">
            <ul className="space-y-1">
              {headings.map((heading) => (
                <li key={heading.id}>
                  <button
                    onClick={() => scrollToSection(heading.id)}
                    data-testid={`toc-item-${heading.id}`}
                    className={`
                      w-full text-left px-3 py-2 rounded-lg text-sm transition-all duration-200
                      ${heading.level === 3 ? 'pl-6' : ''}
                      ${activeId === heading.id
                        ? 'bg-maroon/10 text-maroon font-medium border-l-2 border-maroon'
                        : 'text-muted-foreground hover:text-foreground hover:bg-silver/10'
                      }
                    `}
                  >
                    <span className="line-clamp-2">{heading.text}</span>
                  </button>
                </li>
              ))}
            </ul>
          </nav>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default TableOfContents;
